/**
 * One card per Agent on the Team settings sheet: Bailey, Reeve, Wright,
 * Proctor and Fletcher (Harper has its own file, because its lines are posts).
 *
 * Every card reads the same way: the Agent's mark, name and role, and at the
 * right of the head a fact when there is one. How far the Agent goes on its
 * own is the Team's ladder step, set once at the top of the sheet
 * (`LadderField`); per-Agent rungs are God Mode's override and are not shown
 * here. Then its settings, "With these
 * instructions", Supervised by while it asks, and the acts: a run button only
 * where asking makes sense (Bailey proposes now, Proctor and Fletcher take a
 * pull request), Turn off and Remove. An Agent not on the Team is a dashed
 * card with one button that puts it on.
 */
import { useState, type ReactNode } from "react";
import type { Job, JobAuthority, Schedule, ScheduleDay } from "@yagni/shared";
import {
  DEFAULT_REVIEW_LEVEL,
  REVIEW_LEVELS,
  isReviewLevel,
  type ReviewLevel,
} from "@yagni/shared";

import { ReviewAppInstallNote } from "../../connections/ReviewAppInstallNote.js";
import { Btn } from "../../queue/index.js";
import { RhythmEditor } from "../../workers/RhythmEditor.js";
import { WEEKDAYS, WEEK_ORDER } from "../../workers/scheduleSentence.js";
import { ChecksRoster } from "../ChecksRoster.js";
import {
  LINE_APPROVE_GITHUB_AUTONOMOUS,
  LINE_APPROVE_GITHUB_HINT,
  LINE_APPROVE_GITHUB_LABEL,
  LINE_APPROVE_GITHUB_SUPERVISED,
  LINE_NOTE_PLACEHOLDER,
  BUILD_RUNG_NOTE,
  REVIEW_LEVEL_COPY,
  UI_PREVIEW_HELP_OFF,
  UI_PREVIEW_HELP_ON,
  UI_PREVIEW_LABEL,
  UI_PREVIEW_OFF,
  UI_PREVIEW_ON,
} from "../copy.js";
import { segmentSentence, sentenceSourceFor, sentenceText } from "../jobSentence.js";
import { ReadsChoice, type ReadsMode } from "../ReadsChoice.js";
import { RunNowPicker } from "../RunNowPicker.js";
import { engageAgent, patchJob, runJobNow } from "../teamsApi.js";
import { attachedRepos } from "../useTeamPage.js";
import { defaultSupervisor, type SettingsCtx } from "./context.js";
import {
  AgentCard,
  AreaField,
  ChipList,
  Field,
  HeadFact,
  Mark,
  NumberField,
  Seg,
  Sect,
  SelectField,
} from "./fields.js";
import {
  CRAFT_OF_SECTION,
  WHOSE_ANYONE,
  ladderRungOf,
  lineFor,
  readPath,
  readsAttachment,
  readsModeOf,
  whoseIsPerson,
  writePath,
  type LineDraft,
  type AgentSettingsSection,
} from "./settingsDraft.js";

/** The Agents' own words, for a workspace that has not renamed them and a card with no catalog row. */
export const AGENT_DEFAULTS: Record<
  AgentSettingsSection,
  { name: string; role: string; sub: string }
> = {
  bailey: {
    name: "Bailey",
    role: "Product manager",
    sub: "Proposes tickets from the backlog on a rhythm, writes the spec, and routes questions to the people under Asks.",
  },
  reeve: {
    name: "Reeve",
    role: "Staff engineer",
    sub: "Plans accepted tickets. Fans out the concerns below on consequential changes and asks before it assumes.",
  },
  wright: {
    name: "Wright",
    role: "Builder",
    sub: "Builds the approved plan in a sandbox and opens the pull request. A person merges, unless the Team is on Autonomous + auto-merge.",
  },
  proctor: {
    name: "Proctor",
    role: "Reviewer",
    sub: "Reviews every pull request the Team owns the moment it opens, then iterates with Wright until it can stand behind it.",
  },
  fletcher: {
    name: "Fletcher",
    role: "QA",
    sub: "Tests the pull request on the repository's environment once review marks it ready, and signs off releases.",
  },
  harper: {
    name: "Harper",
    role: "Comms",
    sub: "Writes to Slack. The presets work with no instructions; a custom post is yours to shape.",
  },
};

/** What every Agent card shares: the head, the not-on-the-Team state, Supervised by, instructions and the acts. */
export function AgentFrame({
  ctx,
  section,
  main,
  headRight,
  run,
  children,
  instructions = true,
  more,
}: {
  ctx: SettingsCtx;
  section: AgentSettingsSection;
  /** The line whose rung, switch and instructions the card's head and foot drive. */
  main: Job | null;
  /** A fact at the right of the head. */
  headRight?: ReactNode;
  /** The card's run button, when it has one. */
  run?: ReactNode;
  children?: ReactNode;
  instructions?: boolean;
  /** The Agent's other lines, each with its own supervisor and switch, below the main line's acts. */
  more?: ReactNode;
}) {
  const craft = CRAFT_OF_SECTION[section];
  const agent = ctx.agentFor(craft);
  const words = AGENT_DEFAULTS[section];
  const name = agent?.name ?? words.name;
  const role = words.role;
  const engaged =
    ctx.page.groups.some((group) => group.craft === craft) || (agent?.engaged ?? false);
  const mark = agent ? (
    <Mark avatar={agent.avatar} name={name} slug={agent.personaSlug} size={30} />
  ) : null;

  if (!engaged || !main) {
    return (
      <section
        className="agentset dashed"
        id={`set-${section}`}
        data-section={section}
        data-testid={`settings-${section}`}
      >
        <div className="ah">
          {mark}
          <span style={{ minWidth: 0, flex: 1 }}>
            <div className="nm">{name}</div>
            <div className="role">{role}</div>
            <div className="sub">{words.sub}</div>
          </span>
          {agent && !engaged && !ctx.readOnly && (
            <Btn
              variant="secondary"
              size="sm"
              disabled={ctx.busy}
              data-testid={`settings-add-${section}`}
              onClick={() =>
                void ctx.act(
                  () => engageAgent(ctx.deskId, agent.personaSlug),
                  `${name} is on the Team.`,
                )
              }
            >
              Add {name}
            </Btn>
          )}
        </div>
        {engaged && !main && <p className="fact">{name} has no line on this Team yet.</p>}
      </section>
    );
  }

  const draft = ctx.line(main);
  const kind = ctx.kindOf(main.kind);
  const capped = kind?.ceiling === "supervised";
  // The rung follows the Team's ladder step; the line's own only while the
  // Team reads "Custom (override)".
  const rung: JobAuthority = capped ? "supervised" : ladderRungOf(ctx.team.autonomy, main, draft);
  const set = (update: (d: LineDraft) => LineDraft) => ctx.setLine(main, update);
  const disabled = ctx.readOnly || ctx.busy;

  let head: ReactNode = headRight ?? null;
  if (head === null && main.authority !== null && capped) {
    head = <HeadFact>This one always waits for a person.</HeadFact>;
  }

  const supervisorOptions = supervisorOptionsOf(ctx);
  const supervisor = draft.supervisorUserId ?? defaultSupervisor(ctx) ?? "";

  return (
    <AgentCard id={section} mark={mark} name={name} role={role} sub={words.sub} headRight={head}>
      {children}
      {instructions && (
        <AreaField
          label="With these instructions"
          value={draft.note}
          placeholder={LINE_NOTE_PLACEHOLDER}
          disabled={disabled}
          testId={`settings-${section}-note`}
          help={
            ctx.page.playbookRules && ctx.page.playbookRules.length > 0
              ? `Read on every run, above the Playbook's ${ctx.page.playbookRules.length} ${ctx.page.playbookRules.length === 1 ? "rule" : "rules"}.`
              : undefined
          }
          onChange={(note) => set((d) => ({ ...d, note }))}
        />
      )}
      {main.authority !== null && rung === "supervised" && supervisorOptions.length > 0 && (
        <SelectField
          label="Supervised by"
          value={supervisor}
          disabled={disabled}
          testId={`settings-${section}-supervisor`}
          options={supervisorOptions}
          onChange={(userId) => set((d) => ({ ...d, supervisorUserId: userId || null }))}
        />
      )}
      {!ctx.readOnly && (
        <AgentActs
          ctx={ctx}
          section={section}
          main={main}
          name={name}
          slug={agent?.personaSlug ?? main.personaSlug}
          run={run}
        />
      )}
      {more}
    </AgentCard>
  );
}

/** The Team's members, as a Supervised by picker lists them. */
function supervisorOptionsOf(ctx: SettingsCtx): Array<{ value: string; label: string }> {
  return ctx.page.members.map((member) => ({
    value: member.userId,
    label: member.displayName?.trim() || member.email || member.userId,
  }));
}

function AgentActs({
  ctx,
  section,
  main,
  name,
  slug,
  run,
}: {
  ctx: SettingsCtx;
  section: string;
  main: Job;
  name: string;
  slug: string;
  run?: ReactNode;
}) {
  const draft = ctx.line(main);
  return (
    <div className="acts">
      {run}
      <Btn
        variant="quiet"
        size="sm"
        disabled={ctx.busy}
        data-testid={`settings-${section}-toggle`}
        onClick={() => {
          const active = !main.active;
          void ctx
            .act(
              () => patchJob(ctx.deskId, main.id, { active }),
              active
                ? `${name} is on.`
                : `${name} is off. Nothing new starts until it is turned on.`,
            )
            .then((ok) => {
              if (ok) ctx.setLine(main, (d) => ({ ...d, active }));
            });
        }}
      >
        {draft.active && main.active ? "Turn off" : "Turn on"}
      </Btn>
      <Btn
        variant="quiet"
        size="sm"
        style={{ color: "var(--incident)" }}
        disabled={ctx.busy}
        data-testid={`settings-${section}-remove`}
        onClick={() => {
          if (
            !window.confirm(
              `Take ${name} off this Team? Its lines stop. Its record stays on the Team.`,
            )
          )
            return;
          void ctx.act(
            () =>
              fetch(
                `/api/workers/${encodeURIComponent(slug)}/engagements/${encodeURIComponent(ctx.deskId)}`,
                {
                  method: "DELETE",
                  credentials: "include",
                },
              ).then(async (res) => {
                if (!res.ok) {
                  const body = (await res.json().catch(() => null)) as { error?: string } | null;
                  throw new Error(
                    body?.error ?? `Could not take ${name} off the Team (${res.status}).`,
                  );
                }
              }),
            `${name} is off this Team.`,
          );
        }}
      >
        Remove
      </Btn>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bailey
// ---------------------------------------------------------------------------

const EVERY_DAY: ScheduleDay[] = [...WEEK_ORDER];

/** The three rhythms most Teams pick, as chips. Custom opens the editor. */
export const PICK_PRESETS: ReadonlyArray<{
  id: string;
  label: string;
  times: string[];
  days: ScheduleDay[];
}> = [
  { id: "weekdays", label: "Weekdays at 09:00", times: ["09:00"], days: [...WEEKDAYS] },
  { id: "everyday", label: "Every day at 09:00", times: ["09:00"], days: EVERY_DAY },
  {
    id: "twice",
    label: "Weekdays at 09:00 and 17:00",
    times: ["09:00", "17:00"],
    days: [...WEEKDAYS],
  },
];

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((entry) => b.includes(entry));
}

/** Which preset a schedule is, or null for a custom one. */
export function presetOf(schedule: Schedule | null | undefined): string | null {
  if (!schedule || schedule.every || !schedule.times) return null;
  const days = schedule.days && schedule.days.length > 0 ? schedule.days : EVERY_DAY;
  return (
    PICK_PRESETS.find(
      (preset) => sameSet(preset.times, schedule.times ?? []) && sameSet(preset.days, days),
    )?.id ?? null
  );
}

const TRACKER_NAME: Record<string, string> = { jira: "Jira", linear: "Linear" };

/**
 * Bailey's take line (`pm.take_assigned`): what people assign to yagni in the
 * Team's tracker lands here as a proposal. Supervised only, so it carries a
 * Supervised by picker and its own switch, never a rung. Its sentence is the
 * catalog's, with the tracker named by the backlog the Team reads.
 */
function TakeAssignedLine({
  ctx,
  line,
  agentName,
}: {
  ctx: SettingsCtx;
  line: Job;
  agentName: string;
}) {
  const draft = ctx.line(line);
  const kind = ctx.kindOf(line.kind);
  const disabled = ctx.readOnly || ctx.busy;
  // The tracker the Team reads names the sentence. A Team that reads one
  // tracker still takes an assignment from the other; the line is the same.
  const tracker = TRACKER_NAME[readsAttachment(ctx.page.attachments)?.source ?? ""] ?? null;
  const sentence = kind
    ? sentenceText(
        segmentSentence(kind, sentenceSourceFor(line), {
          agentName,
          nameOf: (slot, value) =>
            slot === "tracker" && (value === undefined || value === "") ? tracker : null,
        }),
      )
    : null;
  const options = supervisorOptionsOf(ctx);
  const supervisor = draft.supervisorUserId ?? defaultSupervisor(ctx) ?? "";
  return (
    <div data-testid="settings-bailey-take">
      <Sect>Assigned tickets</Sect>
      {sentence && (
        <p className="fact" data-testid="settings-bailey-take-sentence">
          {sentence}
        </p>
      )}
      {options.length > 0 && (
        <SelectField
          label="Supervised by"
          value={supervisor}
          disabled={disabled}
          testId="settings-bailey-take-supervisor"
          options={options}
          onChange={(userId) =>
            ctx.setLine(line, (d) => ({ ...d, supervisorUserId: userId || null }))
          }
        />
      )}
      {!ctx.readOnly && (
        <div className="acts">
          <Btn
            variant="quiet"
            size="sm"
            disabled={ctx.busy}
            data-testid="settings-bailey-take-toggle"
            onClick={() => {
              const active = !line.active;
              void ctx
                .act(
                  () => patchJob(ctx.deskId, line.id, { active }),
                  active
                    ? `${agentName} takes assigned tickets.`
                    : `${agentName} no longer takes assigned tickets.`,
                )
                .then((ok) => {
                  if (ok) ctx.setLine(line, (d) => ({ ...d, active }));
                });
            }}
          >
            {draft.active && line.active ? "Turn off" : "Turn on"}
          </Btn>
        </div>
      )}
    </div>
  );
}

export function BaileySection({ ctx }: { ctx: SettingsCtx }) {
  const propose = lineFor(ctx.page.jobs, "pm.propose");
  const take = lineFor(ctx.page.jobs, "pm.take_assigned");
  const [custom, setCustom] = useState(false);
  if (!propose) return <AgentFrame ctx={ctx} section="bailey" main={null} />;

  const draft = ctx.line(propose);
  const disabled = ctx.readOnly || ctx.busy;
  const mode = readsModeOf(draft.active, ctx.team.whose);
  const trigger = draft.trigger.type === "clock" ? draft.trigger : null;
  const schedule = trigger?.schedule ?? null;
  const preset = presetOf(schedule);
  const perDay = readPath(draft.filter, "perDay");
  const people = ctx.people;

  const setMode = (next: ReadsMode) => {
    ctx.setLine(propose, (d) => ({ ...d, active: next !== "hand" }));
    if (next === "filter" && whoseIsPerson(ctx.team.whose))
      ctx.setTeam((t) => ({ ...t, whose: WHOSE_ANYONE }));
    if (next === "me" && !whoseIsPerson(ctx.team.whose)) {
      const me =
        ctx.viewerId && people.some((person) => person.userId === ctx.viewerId)
          ? ctx.viewerId
          : (people[0]?.userId ?? null);
      if (me) ctx.setTeam((t) => ({ ...t, whose: me }));
    }
  };

  const setSchedule = (next: Schedule) =>
    ctx.setLine(propose, (d) => ({
      ...d,
      trigger: {
        type: "clock",
        schedule: {
          ...next,
          ...(schedule?.tz !== undefined && next.tz === undefined ? { tz: schedule.tz } : {}),
        },
        timezone: trigger?.timezone ?? null,
      },
    }));

  const tz = trigger?.timezone ?? schedule?.tz ?? null;

  return (
    <AgentFrame
      ctx={ctx}
      section="bailey"
      main={propose}
      more={
        take ? (
          <TakeAssignedLine
            ctx={ctx}
            line={take}
            agentName={ctx.agentFor("pm")?.name ?? AGENT_DEFAULTS.bailey.name}
          />
        ) : null
      }
      run={
        <Btn
          variant="quiet"
          size="sm"
          disabled={ctx.busy || !propose.active}
          data-testid="settings-bailey-run"
          onClick={() =>
            void ctx.act(
              () => runJobNow(ctx.deskId, propose.id),
              "Bailey is proposing now. The proposals land in Needs you.",
            )
          }
        >
          Propose now
        </Btn>
      }
    >
      <Field
        label="Backlog"
        top
        help="What Bailey sees, as Jira answers it. Whose tickets is set on the Team above."
        testId="settings-bailey-backlog"
      >
        <ReadsChoice
          deskId={ctx.deskId}
          name={`reads-${ctx.deskId}`}
          mode={mode}
          onModeChange={setMode}
          filter={ctx.team.filter}
          onFilterChange={(filter) => ctx.setTeam((t) => ({ ...t, filter }))}
          tracker={ctx.team.tracker}
          trackers={ctx.page.trackers.length > 0 ? ctx.page.trackers : ["jira"]}
          onTrackerChange={(tracker) => ctx.setTeam((t) => ({ ...t, tracker }))}
          person={whoseIsPerson(ctx.team.whose) ? ctx.team.whose : null}
          people={people}
          onPersonChange={(userId) => ctx.setTeam((t) => ({ ...t, whose: userId }))}
          cap={typeof perDay === "number" ? perDay : 3}
          disabled={disabled}
          addJustBlocked={ctx.editsBesideFilter === true}
          foldOnAccept
          onAnswer={(answer) =>
            ctx.setMatches(
              answer && answer.kind !== "error" && typeof answer.total === "number"
                ? answer.total
                : null,
            )
          }
        />
      </Field>
      {trigger && (
        <Field
          label="Picks"
          top
          help={`Custom opens the rhythm editor: fixed times or every so often, days, timezone.${tz ? ` ${tz}.` : ""}`}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
            <div
              className="tags"
              role="radiogroup"
              aria-label="Picks"
              data-testid="settings-bailey-picks"
            >
              {PICK_PRESETS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  role="radio"
                  aria-checked={preset === entry.id && !custom}
                  className={preset === entry.id && !custom ? "chip on" : "chip"}
                  disabled={disabled}
                  onClick={() => {
                    setCustom(false);
                    setSchedule({ times: entry.times, days: entry.days });
                  }}
                >
                  {entry.label}
                </button>
              ))}
              <button
                type="button"
                role="radio"
                aria-checked={custom || preset === null}
                className={custom || preset === null ? "chip on" : "chip add"}
                disabled={disabled}
                data-testid="settings-bailey-custom"
                onClick={() => setCustom((open) => !open)}
              >
                Custom
              </button>
            </div>
            {custom && (
              <RhythmEditor
                schedule={schedule}
                tz={tz}
                fallback={{ times: ["09:00"], days: [...WEEKDAYS] }}
                disabled={disabled}
                onChange={setSchedule}
              />
            )}
          </div>
        </Field>
      )}
      <NumberField
        label="Tickets a day"
        value={typeof perDay === "number" ? perDay : undefined}
        placeholder="3"
        min={1}
        max={10}
        disabled={disabled}
        testId="settings-bailey-perday"
        onChange={(value) =>
          ctx.setLine(propose, (d) => ({ ...d, filter: writePath(d.filter, "perDay", value) }))
        }
        help="Accepts up to this many of its own proposals into Plan each day, 1 to 10, counted in the line's timezone. Nobody is asked to accept them."
      />
    </AgentFrame>
  );
}

// ---------------------------------------------------------------------------
// Reeve
// ---------------------------------------------------------------------------

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

export function ReeveSection({ ctx }: { ctx: SettingsCtx }) {
  const plan = lineFor(ctx.page.jobs, "staff.plan");
  if (!plan) return <AgentFrame ctx={ctx} section="reeve" main={null} />;
  const draft = ctx.line(plan);
  const disabled = ctx.readOnly || ctx.busy;
  // Absent reads as every plan, the default since 2026-10-19 (ADR-0052).
  const scope =
    readPath(draft.filter, "planReviewScope") === "consequential" ? "consequential" : "every";
  return (
    <AgentFrame ctx={ctx} section="reeve" main={plan}>
      <SelectField
        label="How much it checks"
        value={scope}
        disabled={disabled}
        testId="settings-reeve-scope"
        options={[
          { value: "consequential", label: "Consequential changes only" },
          { value: "every", label: "Every plan" },
        ]}
        onChange={(value) =>
          ctx.setLine(plan, (d) => ({
            ...d,
            filter: writePath(d.filter, "planReviewScope", value),
          }))
        }
      />
      <Field
        label={UI_PREVIEW_LABEL}
        testId="settings-reeve-ui-preview"
        help={ctx.team.uiPreview ? UI_PREVIEW_HELP_ON : UI_PREVIEW_HELP_OFF}
      >
        <span>
          <Seg
            label={UI_PREVIEW_LABEL}
            value={ctx.team.uiPreview ? "on" : "off"}
            disabled={disabled}
            options={[
              { value: "on", label: UI_PREVIEW_ON },
              { value: "off", label: UI_PREVIEW_OFF },
            ]}
            onChange={(value) => ctx.setTeam((team) => ({ ...team, uiPreview: value === "on" }))}
          />
        </span>
      </Field>
      <Sect>Concerns · what gets raised when a plan touches them</Sect>
      <ChecksRoster
        deskId={ctx.deskId}
        workflow="plan_review"
        off={stringList(readPath(draft.filter, "disabledRoles"))}
        disabled={disabled}
        onOffChange={(off) =>
          ctx.setLine(plan, (d) => ({ ...d, filter: writePath(d.filter, "disabledRoles", off) }))
        }
        drafts={ctx.checkDrafts}
        onDraftsChange={ctx.setCheckDrafts}
      />
    </AgentFrame>
  );
}

// ---------------------------------------------------------------------------
// Wright
// ---------------------------------------------------------------------------

export function WrightSection({ ctx }: { ctx: SettingsCtx }) {
  const build = lineFor(ctx.page.jobs, "builder.build");
  const fix = lineFor(ctx.page.jobs, "builder.fix_round");
  if (!build) return <AgentFrame ctx={ctx} section="wright" main={null} />;
  // The Build rung only decides a build that did not pass every check; say
  // which way, for the Team's ladder step as the sheet has it now.
  const buildKind = ctx.kindOf(build.kind);
  const buildRung =
    buildKind?.ceiling === "supervised"
      ? "supervised"
      : ladderRungOf(ctx.team.autonomy, build, ctx.line(build));
  return (
    <AgentFrame ctx={ctx} section="wright" main={build}>
      {build.authority !== null && (
        <Field label="Checks not passing">
          <span className="fact" style={{ fontSize: 12 }} data-testid="settings-wright-rung-note">
            {BUILD_RUNG_NOTE[buildRung]}
          </span>
        </Field>
      )}
      {fix && (
        <Field label="Fix rounds">
          <span className="fact" style={{ fontSize: 12 }} data-testid="settings-wright-rounds">
            As many as the review needs, from the ticket's budget. When it is spent, Wright asks you
            to give it more.
          </span>
        </Field>
      )}
    </AgentFrame>
  );
}

// ---------------------------------------------------------------------------
// Proctor
// ---------------------------------------------------------------------------

export function ProctorSection({ ctx }: { ctx: SettingsCtx }) {
  const review = lineFor(ctx.page.jobs, "reviewer.review_pr");
  const [asking, setAsking] = useState(false);
  if (!review) return <AgentFrame ctx={ctx} section="proctor" main={null} />;
  const draft = ctx.line(review);
  const kind = ctx.kindOf(review.kind);
  const disabled = ctx.readOnly || ctx.busy;
  const teamRepos = attachedRepos(ctx.page.attachments);
  const chosen = stringList(readPath(draft.filter, "repos"));
  const stored = readPath(review.filter, "reviewLevel");
  const raw = readPath(draft.filter, "reviewLevel");
  const level: ReviewLevel = isReviewLevel(raw) ? raw : DEFAULT_REVIEW_LEVEL;
  const setFilter = (path: string, value: unknown) =>
    ctx.setLine(review, (d) => ({ ...d, filter: writePath(d.filter, path, value) }));
  // The Team's ladder step picks the rung; the rung alone decides approval
  // (ADR-0047, amended 2026-10-13), so this line follows the draft step.
  const approves =
    kind?.ceiling !== "supervised" &&
    ladderRungOf(ctx.team.autonomy, review, draft) === "autonomous";

  return (
    <AgentFrame
      ctx={ctx}
      section="proctor"
      main={review}
      run={
        kind ? (
          <Btn
            variant="quiet"
            size="sm"
            on={asking}
            disabled={ctx.busy}
            data-testid="settings-proctor-run"
            onClick={() => setAsking((open) => !open)}
          >
            Review a pull request now
          </Btn>
        ) : undefined
      }
    >
      <Field label="Runs on">
        <span className="fact" style={{ fontSize: 12 }}>
          pr.opened
        </span>
      </Field>
      <Field label="Repositories" top testId="settings-proctor-repos">
        <div className="checklist">
          {teamRepos.map((repo) => (
            <label key={repo}>
              <input
                type="checkbox"
                checked={chosen.includes(repo)}
                disabled={disabled}
                onChange={(event) =>
                  setFilter(
                    "repos",
                    event.target.checked
                      ? [...chosen, repo]
                      : chosen.filter((entry) => entry !== repo),
                  )
                }
              />
              <span>{repo}</span>
            </label>
          ))}
          <div className="help" style={{ padding: "2px 8px" }}>
            {teamRepos.length === 0
              ? "The Team works on no repository yet. Add one under Works on."
              : "Unchecked means every repository on this Team."}
          </div>
        </div>
      </Field>
      <ChipList
        label="Paths"
        items={stringList(readPath(draft.filter, "paths"))}
        addLabel="add a path"
        placeholder="e.g. apps/web/**"
        disabled={disabled}
        help="Only pull requests touching these. Empty means all."
        onChange={(items) => setFilter("paths", items)}
        testId="settings-proctor-paths"
      />
      <ChipList
        label="Excluded authors"
        items={stringList(readPath(draft.filter, "authorsExcluded"))}
        addLabel="add an author"
        placeholder="e.g. dependabot[bot]"
        disabled={disabled}
        onChange={(items) => setFilter("authorsExcluded", items)}
        testId="settings-proctor-authors"
      />
      <ChipList
        label="Excluded paths"
        items={stringList(readPath(draft.filter, "pathsExcluded"))}
        addLabel="add a path"
        placeholder="e.g. **/generated/**"
        disabled={disabled}
        onChange={(items) => setFilter("pathsExcluded", items)}
        testId="settings-proctor-excluded"
      />
      <Field label={LINE_APPROVE_GITHUB_LABEL} top testId="settings-proctor-github">
        <div>
          <div className="help">
            {approves ? LINE_APPROVE_GITHUB_AUTONOMOUS : LINE_APPROVE_GITHUB_SUPERVISED}{" "}
            {LINE_APPROVE_GITHUB_HINT}
          </div>
          {/* Informs only: the setting saves whether or not the App is there. */}
          <ReviewAppInstallNote
            repos={chosen.length > 0 ? chosen : teamRepos}
            enabled={approves}
            variant="help"
            testId="settings-proctor-review-app"
          />
        </div>
      </Field>
      <Field label="Review level" top>
        <div>
          <Seg
            label="Review level"
            className="lvl"
            value={level}
            disabled={disabled}
            testId="settings-proctor-level"
            options={REVIEW_LEVELS.map((value) => ({
              value,
              label: REVIEW_LEVEL_COPY[value].label,
            }))}
            onChange={(value) =>
              setFilter(
                "reviewLevel",
                value === DEFAULT_REVIEW_LEVEL && stored === undefined ? undefined : value,
              )
            }
          />
          <div className="help" style={{ marginTop: 6 }}>
            {REVIEW_LEVEL_COPY[level].note}
          </div>
        </div>
      </Field>
      <Sect>Checks · lenses inside the one review, never a second reviewer</Sect>
      <ChecksRoster
        deskId={ctx.deskId}
        workflow="code_review"
        off={stringList(readPath(draft.filter, "checksOff"))}
        disabled={disabled}
        onOffChange={(off) => setFilter("checksOff", off)}
        drafts={ctx.checkDrafts}
        onDraftsChange={ctx.setCheckDrafts}
      />
      {asking && kind && (
        <div className="addform" data-testid="settings-proctor-ask">
          <RunNowPicker
            kind={kind}
            deskId={ctx.deskId}
            filter={draft.filter}
            teamRepos={teamRepos}
            disabled={ctx.readOnly}
            onClose={() => setAsking(false)}
            onAsked={() => void ctx.page.refetch()}
          />
        </div>
      )}
    </AgentFrame>
  );
}

// ---------------------------------------------------------------------------
// Fletcher
// ---------------------------------------------------------------------------

const RELEASE_ENVIRONMENTS = ["staging", "production"] as const;

export function FletcherSection({ ctx }: { ctx: SettingsCtx }) {
  const verify = lineFor(ctx.page.jobs, "qa.verify_pr");
  const signOff = lineFor(ctx.page.jobs, "qa.sign_off_release");
  const [asking, setAsking] = useState(false);
  if (!verify) return <AgentFrame ctx={ctx} section="fletcher" main={null} />;
  const draft = ctx.line(verify);
  const kind = ctx.kindOf(verify.kind);
  const disabled = ctx.readOnly || ctx.busy;
  const steps = readPath(draft.filter, "maxSteps");
  const signDraft = signOff ? ctx.line(signOff) : null;
  const deploys = signDraft
    ? readPath(signDraft.filter, "environments") === undefined
      ? ["staging"]
      : stringList(readPath(signDraft.filter, "environments"))
    : [];
  const nightly = signDraft ? readPath(signDraft.filter, "nightlyAt") : undefined;
  const environment = ctx.page.environments[0];

  return (
    <AgentFrame
      ctx={ctx}
      section="fletcher"
      main={verify}
      headRight={<HeadFact>Runs on pr.ready</HeadFact>}
      run={
        kind ? (
          <Btn
            variant="quiet"
            size="sm"
            on={asking}
            disabled={ctx.busy}
            data-testid="settings-fletcher-run"
            onClick={() => setAsking((open) => !open)}
          >
            Test a pull request now
          </Btn>
        ) : undefined
      }
    >
      <Field label="Environment">
        <span className="fact" style={{ fontSize: 12 }}>
          the repository's environment{environment ? ` · ${environment}` : " · none published yet"}
        </span>
      </Field>
      <NumberField
        label="Steps each run"
        value={typeof steps === "number" ? steps : undefined}
        placeholder="40"
        min={1}
        max={200}
        disabled={disabled}
        testId="settings-fletcher-steps"
        help="The budget for one verification. A run that hits it stops and says so."
        onChange={(value) =>
          ctx.setLine(verify, (d) => ({ ...d, filter: writePath(d.filter, "maxSteps", value) }))
        }
      />
      {signOff && signDraft && (
        <>
          <Sect>Release sign-off</Sect>
          <Field label="Deploys" top>
            <div className="checklist" data-testid="settings-fletcher-deploys">
              {RELEASE_ENVIRONMENTS.map((env) => (
                <label key={env}>
                  <input
                    type="checkbox"
                    checked={deploys.includes(env)}
                    disabled={disabled}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? [...deploys, env]
                        : deploys.filter((entry) => entry !== env);
                      ctx.setLine(signOff, (d) => ({
                        ...d,
                        filter: { ...d.filter, environments: next },
                      }));
                    }}
                  />
                  <span>{env}</span>
                </label>
              ))}
            </div>
          </Field>
          <Field label="Nightly at">
            <div className="in mono" style={{ maxWidth: 140 }}>
              <input
                type="time"
                aria-label="Nightly at"
                value={typeof nightly === "string" ? nightly : ""}
                disabled={disabled}
                data-testid="settings-fletcher-nightly"
                onChange={(event) =>
                  ctx.setLine(signOff, (d) => ({
                    ...d,
                    filter: writePath(d.filter, "nightlyAt", event.target.value || undefined),
                  }))
                }
              />
            </div>
          </Field>
        </>
      )}
      {asking && kind && (
        <div className="addform" data-testid="settings-fletcher-ask">
          <RunNowPicker
            kind={kind}
            deskId={ctx.deskId}
            filter={draft.filter}
            teamRepos={attachedRepos(ctx.page.attachments)}
            disabled={ctx.readOnly}
            onClose={() => setAsking(false)}
            onAsked={() => void ctx.page.refetch()}
          />
        </div>
      )}
    </AgentFrame>
  );
}
